// PlatformStubPage — CHG-1551 / ITER-3668. Shared "Coming soon" stub
// for the 5 forwardPromised /platform/* routes that PlatformLinksCard
// + the Platform sidebar group surface. Pre-fix (field-test report B3 +
// N3), the 5 SOON-badged rows were palette-discoverable and clickable
// but every URL hit the generic NotFound page — confusing the user
// (the SOON badge promised "not yet, but coming" not "broken").
//
// Post-fix: each route mounts this component. Renders consistent
// AppShell chrome (DashboardPage shell, page title, SOON badge,
// back-to-/platform link). When the real per-surface page lands in a
// future CHG it replaces the route's element prop — no other call
// sites change.

import { useParams, Link } from "react-router-dom";
import { DashboardPage } from "../components/composition/DashboardPage";

export interface PlatformStubPageProps {
  /** Optional title override. When omitted, derive from the URL slug
   *  (e.g., "/platform/audit-logs" → "Audit logs"). */
  title?: string;
  /** Optional subtitle override. Defaults to a generic "Coming soon"
   *  copy that names the route. */
  subtitle?: string;
}

function titleFromSlug(slug: string | undefined): string {
  if (!slug) return "Platform";
  // "audit-logs" → "Audit logs"; "v2-adoption" → "V2 adoption" etc.
  const words = slug.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function PlatformStubPage({ title, subtitle }: PlatformStubPageProps = {}) {
  const params = useParams<{ slug?: string }>();
  // Routes mount this with explicit titles (preferred), but the params
  // fallback keeps the component robust if it's ever wired into a
  // catch-all /platform/:slug pattern.
  const resolvedTitle = title ?? titleFromSlug(params.slug);
  const resolvedSubtitle =
    subtitle ??
    "This platform-admin surface is coming soon. The route is reserved so deep links won't 404; the real page will land in a follow-up CHG.";

  return (
    <DashboardPage
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
      stats={
        <div
          data-testid="platform-stub-soon-banner"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 12px",
            borderRadius: 9999,
            background: "var(--color-bg-subtle)",
            color: "var(--color-text-muted)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          Coming soon
        </div>
      }
      feed={
        <section
          className="card"
          aria-labelledby="platform-stub-back-title"
          style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}
        >
          <h2
            id="platform-stub-back-title"
            style={{ fontSize: 14, fontWeight: 500, margin: 0 }}
          >
            What's here today
          </h2>
          <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>
            The {resolvedTitle.toLowerCase()} surface isn't built yet. Until it
            lands, the platform dashboard exposes the live data we have
            (organization count, member stats, v2 adoption telemetry).
          </p>
          <Link
            to="/platform"
            data-testid="platform-stub-back-link"
            style={{
              fontSize: 13,
              color: "var(--color-text)",
              textDecoration: "underline",
            }}
          >
            ← Back to Platform dashboard
          </Link>
        </section>
      }
    />
  );
}
